import { ColumnInfo, ColumnInfoList, DisplaySetting } from 'components/shared/display_setting'
import { Flex } from 'components/shared/flex'
import { SelectItem } from 'components/shared/input'
import { TableCell } from 'components/shared/widget'
import { Sample, SMOKING_STATUS_SELECT_ITEMS } from 'entities/sample'
import { ReactNode, useMemo } from 'react'
import { Form } from 'utils/hooks'
import { PatientViewModel } from 'view_models/patient_view_model'
import { SampleViewModel } from 'view_models/sample_view_model'

export const analyticsSampleColumnInfoList = (form?: Form<Record<string, unknown>>, productSelectItems?: SelectItem[]): ColumnInfoList => {
    return {
        tableName: '',
        values: [
            {
                name: '病院・施設',
                th: true,
                width: 200,
                form: form,
                orderAttr: 'sample_institution_id', // ToDo 変更
                searchType: { name: 'institutionName', type: 'multiple_text' },
            },
            {
                name: '患者コード',
                th: true,
                width: 100,
                align: 'center',
                form: form,
                orderAttr: 'patient_code',
                searchType: { name: 'PatientCode', type: 'text' },
            },
            {
                name: '製品',
                th: true,
                width: 130,
                form: form,
                searchType: { name: 'ProductItemId', type: 'multiple_select', selectItems: productSelectItems },
            },
            { name: '年齢', th: true, width: 70, align: 'center', form: form, orderAttr: 'patient_birthday' },
            {
                name: '性別',
                th: true,
                width: 70,
                align: 'center',
                form: form,
                orderAttr: 'patient_gender',
                searchType: { name: 'PatientGender', type: 'text' },
            },
            { name: '喫煙ステータス', th: true, width: 200, align: 'center' },
            { name: '備考(喫煙)', th: true, width: 200 },
            { name: '飲酒ステータス', th: true, width: 120, align: 'center' },
            { name: '備考(飲酒)', th: true, width: 200 },
            {
                name: '妊娠中',
                th: true,
                width: 70,
                align: 'center',
                form: form,
                orderAttr: 'sample_pregnancy',
                searchType: { name: 'Pregnancy', type: 'text' },
            },
            {
                name: '生理中',
                th: true,
                width: 70,
                align: 'center',
                form: form,
                orderAttr: 'sample_menstruation',
                searchType: { name: 'Menstruation', type: 'text' },
            },
            {
                name: 'カルテ番号',
                th: true,
                width: 100,
                align: 'center',
                form: form,
                orderAttr: 'sample_karte_number',
                searchType: { name: 'KarteNumber', type: 'text' },
            },
            { name: '現在治療中の病気', th: true, width: 200 },
            { name: '備考(現在治療中の病気)', th: true, width: 200 },
            { name: '過去にかかった病気', th: true, width: 200 },
            { name: '備考(過去にかかった病気)', th: true, width: 200 },
            { name: '家族歴(続柄/部位)', th: true, width: 200 },
            { name: '備考(家族歴)', th: true, width: 200 },
        ],
    }
}

export type AnalyticsSampleTableDataProps = {
    sample?: Sample
    displaySetting: DisplaySetting
}

export const AnalyticsSampleTableData = ({ sample, displaySetting }: AnalyticsSampleTableDataProps): JSX.Element => {
    if (!sample) {
        return (
            <>
                {displaySetting.columnInfoList.values.map((_: ColumnInfo, index: number) => {
                    if (displaySetting.hiddenList.includes(index)) {
                        return <></>
                    } else {
                        return <TableCell key={index}></TableCell>
                    }
                })}
            </>
        )
    }
    const vm = new SampleViewModel(sample)
    const pvm = new PatientViewModel(sample.patient ?? {})
    const createCells = useMemo(() => {
        return (
            <>
                <TableCell>{sample.institution?.name}</TableCell>
                <TableCell align="center">{sample.patient?.code}</TableCell>
                <TableCell>{vm.productItemView()}</TableCell>
                <TableCell align="center">{pvm.age()}</TableCell>
                <TableCell align="center">{pvm.gender()}</TableCell>
                <TableCell align="center">
                    <div>
                        {sample.smokingStatus ? (
                            <div>
                                <div style={{ fontSize: 12 }}>{SMOKING_STATUS_SELECT_ITEMS.find((s) => s.value === sample.smokingStatus)?.label}</div>
                                {sample.smokingStatus !== 'NEVER' && (
                                    <div style={{ fontSize: 10 }}>
                                        <Flex alignItems="center" justifyContent="center" className="">
                                            <div style={{ marginRight: 4 }}>{sample.smokingPerDay ?? '?'}本/1日</div>
                                            <div style={{ marginRight: 4 }}>
                                                {sample.smokingStartAge ?? '?'}~{sample.smokingEndAge ?? '?'}歳まで
                                            </div>
                                            <div>
                                                {sample.smokingYears
                                                    ? sample.smokingYears
                                                    : !!sample.smokingStartAge && !!sample.smokingEndAge
                                                    ? sample.smokingEndAge - sample.smokingStartAge
                                                    : '?'}
                                                年間
                                            </div>
                                        </Flex>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>?</div>
                        )}
                    </div>
                </TableCell>
                <TableCell>{sample.smokingNote}</TableCell>
                <TableCell align="center">
                    <Flex alignItems="center" justifyContent="center">
                        <div style={{ fontSize: 12 }}>
                            {sample.alcoholPerWeek ? (sample.alcoholPerWeek > 0 ? `飲む(週${sample.alcoholPerWeek}日)` : '飲む(週?日)') : '飲まない'}
                        </div>
                    </Flex>
                </TableCell>
                <TableCell>{sample.alcoholNote}</TableCell>
                <TableCell align="center">{sample.pregnancy ? 'YES' : 'NO'}</TableCell>
                <TableCell align="center">{sample.menstruation ? 'YES' : 'NO'}</TableCell>
                <TableCell align="center">{sample.karteNumber}</TableCell>
                <TableCell>
                    <Flex alignItems="center">
                        {sample.currentMedicalHistories?.map((cmh, index) => {
                            return <div key={'cmh-' + index}>{cmh.disease + (cmh.part ? `(${cmh.part})` : '')}</div>
                        })}
                    </Flex>
                </TableCell>
                <TableCell>{sample.currentMedicalHistoryNote}</TableCell>
                <TableCell>
                    <Flex alignItems="center">
                        {sample.pastMedicalHistories?.map((pmh, index) => {
                            return <div key={'pmh-' + index}>{pmh.disease + (pmh.part ? `(${pmh.part})` : '')}</div>
                        })}
                    </Flex>
                </TableCell>
                <TableCell>{sample.pastMedicalHistoryNote}</TableCell>
                <TableCell>
                    <Flex alignItems="center">
                        {sample.familyHistories?.map((fh, index) => {
                            return (
                                <div key={'fh-' + index}>
                                    {fh.relationship}/{fh.cancer}
                                </div>
                            )
                        })}
                    </Flex>
                </TableCell>
                <TableCell>{sample.familyHistoryNote}</TableCell>
            </>
        )
    }, [displaySetting.hiddenList])

    return createCells.props.children.filter((_: ReactNode, index: number) => !displaySetting.hiddenList?.includes(index)).map((cell: ReactNode) => cell)
}
